/**
 * セキュリティユーティリティ
 * XSS対策、CSRF対策、入力検証などのセキュリティ機能を提供します
 */

/**
 * HTML特殊文字をエスケープしてXSS攻撃を防止
 * @param {string} text - エスケープする文字列
 * @returns {string} - エスケープされた文字列
 */
export const escapeHtml = (text) => {
  if (!text) return '';
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * DOMPurifyのような機能でHTMLをサニタイズ
 * 実際の実装では、DOMPurifyなどの専用ライブラリの使用を推奨
 * @param {string} html - サニタイズするHTML
 * @returns {string} - サニタイズされたHTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  
  // 基本的な危険なタグを除去（実際の実装ではDOMPurifyなどを使用）
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    .replace(/<base\b[^<]*(?:(?!<\/base>)<[^<]*)*<\/base>/gi, '')
    .replace(/on\w+="[^"]*"/gi, ''); // イベントハンドラの除去
};

/**
 * CSRFトークンの生成
 * @returns {string} - 生成されたCSRFトークン
 */
export const generateCsrfToken = () => {
  // ランダムな文字列を生成
  const randomString = Math.random().toString(36).substring(2, 15) + 
                      Math.random().toString(36).substring(2, 15);
  
  // 現在時刻を含める
  const timestamp = new Date().getTime().toString(36);
  
  // トークンを生成して保存
  const token = `${randomString}-${timestamp}`;
  localStorage.setItem('csrfToken', token);
  
  return token;
};

/**
 * CSRFトークンの取得
 * @returns {string} - 保存されたCSRFトークン
 */
export const getCsrfToken = () => {
  let token = localStorage.getItem('csrfToken');
  
  if (!token) {
    token = generateCsrfToken();
  }
  
  return token;
};

/**
 * パスワードの強度チェック
 * @param {string} password - チェックするパスワード
 * @returns {object} - パスワードの強度情報
 */
export const checkPasswordStrength = (password) => {
  if (!password) {
    return { 
      score: 0, 
      isStrong: false,
      message: 'パスワードを入力してください' 
    };
  }
  
  let score = 0;
  const feedback = [];
  
  // 長さチェック
  if (password.length < 8) {
    feedback.push('パスワードは8文字以上である必要があります');
  } else {
    score += 1;
  }
  
  // 大文字を含むか
  if (!/[A-Z]/.test(password)) {
    feedback.push('大文字を含めてください');
  } else {
    score += 1;
  }
  
  // 小文字を含むか
  if (!/[a-z]/.test(password)) {
    feedback.push('小文字を含めてください');
  } else {
    score += 1;
  }
  
  // 数字を含むか
  if (!/[0-9]/.test(password)) {
    feedback.push('数字を含めてください');
  } else {
    score += 1;
  }
  
  // 特殊文字を含むか
  if (!/[^A-Za-z0-9]/.test(password)) {
    feedback.push('特殊文字を含めてください');
  } else {
    score += 1;
  }
  
  const isStrong = score >= 4;
  let message = '';
  
  if (isStrong) {
    message = '強力なパスワードです';
  } else {
    message = feedback.join('、');
  }
  
  return {
    score,
    isStrong,
    message
  };
};

/**
 * メールアドレスの検証
 * @param {string} email - 検証するメールアドレス
 * @returns {boolean} - 有効なメールアドレスかどうか
 */
export const validateEmail = (email) => {
  if (!email) return false;
  
  // 基本的なメールアドレス形式の検証
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * 入力値の検証
 * @param {string} value - 検証する入力値
 * @param {object} options - 検証オプション
 * @returns {object} - 検証結果
 */
export const validateInput = (value, options = {}) => {
  const {
    required = false,
    minLength = 0,
    maxLength = Number.MAX_SAFE_INTEGER,
    pattern = null,
    type = 'text'
  } = options;
  
  // 結果オブジェクト
  const result = {
    isValid: true,
    errors: []
  };
  
  // 必須チェック
  if (required && (!value || value.trim() === '')) {
    result.isValid = false;
    result.errors.push('この項目は必須です');
  }
  
  // 長さチェック
  if (value && value.length < minLength) {
    result.isValid = false;
    result.errors.push(`${minLength}文字以上入力してください`);
  }
  
  if (value && value.length > maxLength) {
    result.isValid = false;
    result.errors.push(`${maxLength}文字以内で入力してください`);
  }
  
  // パターンチェック
  if (pattern && value && !pattern.test(value)) {
    result.isValid = false;
    result.errors.push('入力形式が正しくありません');
  }
  
  // タイプ別チェック
  if (type === 'email' && value && !validateEmail(value)) {
    result.isValid = false;
    result.errors.push('有効なメールアドレスを入力してください');
  }
  
  return result;
};

export default {
  escapeHtml,
  sanitizeHtml,
  generateCsrfToken,
  getCsrfToken,
  checkPasswordStrength,
  validateEmail,
  validateInput
}; 